import { lazy, Suspense, type ReactNode } from "react";
import { useT } from "../i18n/catalogue.js";

import { EmptyState, LoadingState } from "../states/index.js";
import { isEmptyRanked, type RankedBar } from "./ranked.js";

/**
 * A ranked horizontal bar chart — "which of these is biggest, and by how much".
 *
 * ==========================================================================================
 * A DESIGN-SYSTEM ADDITION, MADE DELIBERATELY
 * ==========================================================================================
 *
 * `dashboard.md`'s primitive table has an entry that reads, in full: "Expense categories · `StatCard`
 * rows · A ranking, and no ranked primitive exists". This is that primitive. It was declined in v1 on
 * the same grounds as the waterfall and overturned the same day, for the same reason: the expense
 * summary was ten figures in a column, which is the weakest thing on the page to look at.
 *
 * WHAT IT MUST NOT BECOME is a part-of-whole mark. The bars are scaled to the largest value and never
 * to their sum, because the ten rows are a top-ten of seventeen and do not constitute a whole.
 * `ranked.ts` carries that argument at length, beside the one line that implements it.
 *
 * `lazy` at MODULE SCOPE, as the other charts are.
 */
const Impl = lazy(() =>
  import("./RankedBarChartImpl.js").then((m) => ({ default: m.RankedBarChartImpl })),
);

export interface RankedBarChartProps {
  /** Any order — the chart sorts descending, so a caller cannot ship an unranked ranking. */
  bars: RankedBar[];
  /** How wide the category labels may get. Words, so the default is generous. */
  labelWidth?: number;
  /** Shown when there is nothing to draw. The caller knows what "nothing" means here. */
  emptyMessage?: ReactNode;
}

export function RankedBarChart({ bars, labelWidth, emptyMessage }: RankedBarChartProps): ReactNode {
  const t = useT();
  // Checked BEFORE the boundary, so an empty ranking never downloads Recharts.
  if (isEmptyRanked(bars)) return <EmptyState>{emptyMessage ?? t("ui.chart.empty")}</EmptyState>;

  return (
    <Suspense fallback={<LoadingState>{t("ui.chart.loading")}</LoadingState>}>
      <Impl bars={bars} {...(labelWidth === undefined ? {} : { labelWidth })} />
    </Suspense>
  );
}
